import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, RefreshCw, Search, Store, X } from "lucide-react";
import { AppDispatch, RootState } from "../../../../../dataManagement/uiDb/UiDb";
import { businessSearch } from "../../../../../services/thunks/BusinessThunk";
import { fetchProductsByBusinessId } from "../../../../../services/thunks/ListingCollectionThunk";
import { BusinessProfile } from "../../../../../interfaces/BusinessObject";
import { convertRawProduct } from "../../../../../services/converters/backendToUi/productConvertor";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useBusinessProfilePicture } from "../../../../../hooks/useBusinessProfilePicture";
import "./styles/BusinessSearchAddListings.css";

/** A staged listing — minimal shape needed for chip display and payload building. */
export interface StagedListing {
  productId: string;
  title: string;
  /** Absent in edit mode — the detail response doesn't carry which business
   *  a listing belongs to. */
  businessName?: string;
}

const BusinessResultThumb: React.FC<{ business: BusinessProfile }> = ({ business }) => {
  const logoUrl = useBusinessProfilePicture(business.businessId, business.logoUrl);
  return (
    <span className="biz-lst-add-result-thumb">
      {logoUrl ? <img src={logoUrl} alt="" /> : <Store size={14} color="#6a11cb" />}
    </span>
  );
};

interface BusinessSearchAddListingsProps {
  stagedListings: StagedListing[];
  onListingsChange: (listings: StagedListing[]) => void;
}

/**
 * Single-business listing picker for Create/Edit Listing Collection.
 *
 * Stage 1 — no business selected: show the business search input.
 * Stage 2 — business selected: fetch all its listings, show them as chips.
 *   The search is hidden; a "Change business" button clears everything and
 *   resets to stage 1 so a different business can be picked.
 *
 * Only one business at a time is supported by design. Edit mode pre-populates
 * chips from the existing collection; "Change business" clears them and lets
 * the curator pick a replacement business.
 */
const BusinessSearchAddListings: React.FC<BusinessSearchAddListingsProps> = ({
  stagedListings,
  onListingsChange,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const userPreferences = useSelector(
    (state: RootState) => state.profile.userPreferences
  );

  const [selectedBusiness, setSelectedBusiness] = useState<BusinessProfile | null>(null);
  const [loadingBusinessId, setLoadingBusinessId] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 350);
  const [bizResults, setBizResults] = useState<BusinessProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!trimmed) {
      setBizResults([]);
      setSearchError(null);
      return;
    }

    let cancelled = false;
    setIsSearching(true);
    setSearchError(null);
    dispatch(businessSearch({ searchString: trimmed, userPreferences }))
      .unwrap()
      .then((res) => { if (!cancelled) setBizResults(res); })
      .catch((err) => {
        if (!cancelled) {
          setBizResults([]);
          setSearchError(err instanceof Error ? err.message : "Search failed.");
        }
      })
      .finally(() => { if (!cancelled) setIsSearching(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const handleSelectBusiness = async (business: BusinessProfile) => {
    if (loadingBusinessId) return;
    setLoadingBusinessId(business.businessId);
    setSearchError(null);
    try {
      const data = await dispatch(fetchProductsByBusinessId(business.businessId)).unwrap();
      const rawProducts: Record<string, unknown>[] =
        (data?.contents?.[0]?.products ?? []) as Record<string, unknown>[];

      const listings: StagedListing[] = rawProducts
        .map((raw) => {
          const p = convertRawProduct(raw);
          return {
            productId: p.productId,
            title: p.title ?? "Unnamed listing",
            businessName: business.name,
          };
        })
        .filter((l) => Boolean(l.productId));

      setSelectedBusiness(business);
      setQuery("");
      setBizResults([]);
      onListingsChange(listings);
    } catch {
      setSearchError(`Couldn't load listings for ${business.name}.`);
    } finally {
      setLoadingBusinessId(null);
    }
  };

  const handleChangeBusiness = () => {
    setSelectedBusiness(null);
    setLoadingBusinessId(null);
    setQuery("");
    setBizResults([]);
    setSearchError(null);
    onListingsChange([]);
  };

  const handleRemove = (productId: string) => {
    const next = stagedListings.filter((l) => l.productId !== productId);
    if (next.length === 0) setSelectedBusiness(null);
    onListingsChange(next);
  };

  // Show search when no business is locked in yet.
  const showSearch = !selectedBusiness && stagedListings.length === 0;
  // Show "Change business" when either a business was just picked (create)
  // or listings are pre-populated from an existing collection (edit).
  const showChangeBusiness = selectedBusiness !== null || stagedListings.length > 0;

  return (
    <div className="biz-lst-add">
      {showSearch && (
        <>
          <div className="biz-lst-add-input-row">
            <Search size={16} className="biz-lst-add-icon" />
            <input
              type="text"
              className="biz-lst-add-input"
              placeholder="Search businesses by name..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {isSearching && <Loader2 size={16} className="biz-lst-add-spinner" />}
          </div>

          {searchError && <p className="biz-lst-add-error">{searchError}</p>}

          {query.trim() && (
            <div className="biz-lst-add-results">
              {bizResults.length === 0 && !isSearching && !searchError && (
                <p className="biz-lst-add-empty">No businesses found.</p>
              )}
              {bizResults.map((biz) => {
                const isLoading = loadingBusinessId === biz.businessId;
                return (
                  <button
                    type="button"
                    key={biz.businessId}
                    className="biz-lst-add-result"
                    disabled={loadingBusinessId !== null}
                    onClick={() => handleSelectBusiness(biz)}
                  >
                    <BusinessResultThumb business={biz} />
                    <span className="biz-lst-add-result-name">{biz.name}</span>
                    {isLoading
                      ? <Loader2 size={14} className="biz-lst-add-spinner" />
                      : null
                    }
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {showChangeBusiness && (
        <div className="biz-lst-add-selected-row">
          {selectedBusiness && (
            <span className="biz-lst-add-selected-name">
              <Store size={13} />
              {selectedBusiness.name}
            </span>
          )}
          <button
            type="button"
            className="biz-lst-add-change-btn"
            onClick={handleChangeBusiness}
          >
            <RefreshCw size={13} />
            Change business
          </button>
        </div>
      )}

      {stagedListings.length > 0 && (
        <div className="biz-lst-add-chips-section">
          <span className="biz-lst-add-chips-label">
            Staged listings
            <span className="biz-lst-add-chips-count"> — {stagedListings.length} total</span>
          </span>
          <div className="biz-lst-add-chips">
            {stagedListings.map((listing) => (
              <span className="biz-lst-add-chip" key={listing.productId}>
                <span className="biz-lst-add-chip-title">{listing.title}</span>
                <button
                  type="button"
                  aria-label={`Remove ${listing.title}`}
                  onClick={() => handleRemove(listing.productId)}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessSearchAddListings;
